/**
 * Teams OAuth connect panel — shown in ChannelDetail when teams config mode = "oauth2".
 *
 * Responsibilities:
 *  1. Show connection status (GET /oauth2/teams/status)
 *  2. Connect via popup → postMessage relay from /oauth/callback
 *  3. Team + channel dropdowns populated after connected
 *  4. Save (PUT /channels/:id config.teams = { mode:"oauth2", team_id, channel_id })
 *
 * Listener lifecycle:
 *  - Exactly ONE message listener is active while a popup is open.
 *  - Cleaned up on: message received, popup closed manually (poll), or component unmount.
 *  - `connecting` is always reset to false by any of the above paths → never stuck.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, RefreshCw, Unlink, AlertTriangle } from 'lucide-react';
import {
  Button,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateChannel } from '@/api/healthcheck';
import { hcExtractError } from '@/api/healthcheck/_shared';
import {
  fetchTeamsAuthorizeUrl,
  exchangeOAuthCode,
  teamsOAuthKeys,
  useTeamsOAuthStatus,
  useTeamsTeams,
  useTeamsChannels,
  useTeamsChats,
  useTeamsDisconnect,
} from '@/api/healthcheck/teams-oauth';
import { TeamsDestinationToggle, type TeamsTargetType } from './TeamsDestinationToggle';

interface Props {
  channelId: string;
  /** Current teams config from BE (may contain prior oauth2 team_id / channel_id). */
  currentConfig: Record<string, unknown>;
}

type TeamsConfig = {
  mode: 'oauth2';
  target_type?: TeamsTargetType;
  team_id?: string;
  channel_id?: string;
  chat_id?: string;
};

function extractTeamsOAuthConfig(config: Record<string, unknown>): TeamsConfig {
  const raw = config['teams'] as Record<string, unknown> | undefined;
  // target_type missing → 'channel' (backward-compatible with pre-chat configs).
  const target: TeamsTargetType = raw?.target_type === 'chat' ? 'chat' : 'channel';
  return {
    mode: 'oauth2',
    target_type: target,
    team_id: typeof raw?.team_id === 'string' ? raw.team_id : undefined,
    channel_id: typeof raw?.channel_id === 'string' ? raw.channel_id : undefined,
    chat_id: typeof raw?.chat_id === 'string' ? raw.chat_id : undefined,
  };
}

export function TeamsOAuthPanel({ channelId, currentConfig }: Props) {
  const qc = useQueryClient();
  const { data: status, isLoading: loadingStatus, refetch: refetchStatus } =
    useTeamsOAuthStatus(channelId);

  const initial = extractTeamsOAuthConfig(currentConfig);
  const [targetType, setTargetType] = useState<TeamsTargetType>(initial.target_type ?? 'channel');
  const [teamId, setTeamId] = useState<string>(initial.team_id ?? '');
  const [msChannelId, setMsChannelId] = useState<string>(initial.channel_id ?? '');
  const [chatId, setChatId] = useState<string>(initial.chat_id ?? '');
  const [connectError, setConnectError] = useState<string | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // H1+H2: single active popup + listener tracked in refs.
  // These refs are the source of truth for cleanup in the useEffect below.
  const activePopupRef = useRef<Window | null>(null);
  const activeListenerRef = useRef<((ev: MessageEvent) => void) | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Hard stop if the user never finishes consent (popup left open).
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Unmount guard: never call setState after unmount.
  const mountedRef = useRef(true);

  const FLOW_TIMEOUT_MS = 3 * 60 * 1000;

  const { data: teamsData = [], isLoading: loadingTeams } = useTeamsTeams(
    status?.connected ? channelId : undefined,
  );
  const { data: channelsData = [], isLoading: loadingChannels } = useTeamsChannels(
    status?.connected && targetType === 'channel' && teamId ? channelId : undefined,
    teamId || undefined,
  );
  const { data: chatsData = [], isLoading: loadingChats } = useTeamsChats(
    status?.connected && targetType === 'chat' ? channelId : undefined,
  );

  const disconnect = useTeamsDisconnect(channelId);
  const update = useUpdateChannel(channelId);

  // Reset channel selection when team changes.
  const prevTeamId = useRef(teamId);
  useEffect(() => {
    if (prevTeamId.current !== teamId) {
      setMsChannelId('');
      prevTeamId.current = teamId;
    }
  }, [teamId]);

  // Set mounted flag on mount; clear on unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Teardown helper — removes the active listener, clears the poll interval, and
   * optionally resets `connecting`. Safe to call multiple times (idempotent).
   */
  const teardownOAuthFlow = useCallback((resetConnecting: boolean) => {
    if (activeListenerRef.current) {
      window.removeEventListener('message', activeListenerRef.current);
      activeListenerRef.current = null;
    }
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Close any popup we still hold a handle to (it may already be closed).
    try {
      if (activePopupRef.current && !activePopupRef.current.closed) {
        activePopupRef.current.close();
      }
    } catch {
      // cross-origin / already gone — ignore.
    }
    activePopupRef.current = null;
    if (resetConnecting && mountedRef.current) {
      setConnecting(false);
    }
  }, []);

  // H1: unmount cleanup — always tears down any active listener/interval.
  useEffect(() => {
    return () => {
      teardownOAuthFlow(false); // don't call setState on unmounting tree
    };
  }, [teardownOAuthFlow]);

  const handleConnect = async () => {
    setConnectError(null);
    setPopupBlocked(false);

    // H2: remove any existing listener before starting a new flow.
    teardownOAuthFlow(false);
    setConnecting(true);

    let url: string;
    try {
      url = await fetchTeamsAuthorizeUrl(channelId);
    } catch (e: unknown) {
      if (mountedRef.current) {
        setConnecting(false);
        setConnectError(hcExtractError(e, 'Failed to start OAuth flow'));
      }
      return;
    }

    // M1: removed non-standard `noopener=0`; opener retained by default (required for postMessage).
    const popup = window.open(url, 'teams-oauth', 'width=600,height=700');
    if (!popup) {
      if (mountedRef.current) {
        setPopupBlocked(true);
        setConnecting(false);
      }
      return;
    }
    activePopupRef.current = popup;

    // Single message listener for this flow. The popup is a thin relay: it sends
    // back { code, state, error }; THIS tab (authenticated) does the exchange.
    const onMessage = async (ev: MessageEvent) => {
      // Security: only accept same-origin messages from our own popup.
      if (ev.origin !== window.location.origin) return;
      if (ev.source !== popup) return;
      const data = ev.data as {
        type?: string;
        code?: string;
        state?: string;
        error?: string;
      };
      if (data?.type !== 'oauth-callback') return;

      // Stop the poll/timeout; keep `connecting` true while we exchange.
      teardownOAuthFlow(false);

      if (data.error) {
        if (mountedRef.current) { setConnectError(data.error); setConnecting(false); }
        return;
      }
      if (!data.code || !data.state) {
        if (mountedRef.current) { setConnectError('Missing authorization code.'); setConnecting(false); }
        return;
      }

      try {
        await exchangeOAuthCode(data.code, data.state);
        await qc.invalidateQueries({ queryKey: teamsOAuthKeys.status(channelId) });
        await qc.invalidateQueries({ queryKey: teamsOAuthKeys.teams(channelId) });
        await qc.invalidateQueries({ queryKey: teamsOAuthKeys.chats(channelId) });
        if (mountedRef.current) await refetchStatus();
      } catch (e: unknown) {
        if (mountedRef.current) setConnectError(hcExtractError(e, 'OAuth exchange failed'));
      } finally {
        if (mountedRef.current) setConnecting(false);
      }
    };
    activeListenerRef.current = onMessage;
    window.addEventListener('message', onMessage);

    // Detect the user closing the popup without completing consent (poll 500 ms).
    pollIntervalRef.current = setInterval(() => {
      if (activePopupRef.current?.closed) {
        teardownOAuthFlow(true);
      }
    }, 500);

    // Hard timeout — never leave the button stuck on "Connecting…".
    timeoutRef.current = setTimeout(() => {
      teardownOAuthFlow(true);
      if (mountedRef.current) setConnectError('Connection timed out. Please try again.');
    }, FLOW_TIMEOUT_MS);
  };

  const handleDisconnect = () => {
    disconnect.mutate(undefined, {
      onSuccess: () => {
        setTeamId('');
        setMsChannelId('');
        setChatId('');
      },
    });
  };

  const canSave =
    !update.isPending &&
    (targetType === 'chat' ? !!chatId : !!teamId && !!msChannelId);

  const handleSave = () => {
    if (!canSave) return;
    // Build only the fields for the chosen target — never send the other kind's.
    const teams: TeamsConfig =
      targetType === 'chat'
        ? { mode: 'oauth2', target_type: 'chat', chat_id: chatId }
        : { mode: 'oauth2', target_type: 'channel', team_id: teamId, channel_id: msChannelId };
    update.mutate({ config: { teams } });
  };

  if (loadingStatus) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <Skeleton style={{ height: 36, width: 220 }} />
        <Skeleton style={{ height: 36, width: 160 }} />
      </div>
    );
  }

  const connected = status?.connected ?? false;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Connection status strip — info on the left, actions on the right */}
      <div style={statusRow}>
        {connected ? (
          <>
            <div style={statusInfo}>
              <CheckCircle size={16} color="var(--vn-success-solid)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-success-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Account connected{status?.account_label ? `: ${status.account_label}` : ''}
              </span>
            </div>
            <div style={statusActions}>
              <Button
                size="sm"
                variant="secondary"
                startIcon={<RefreshCw size={13} />}
                onClick={handleConnect}
                disabled={connecting}
              >
                Reconnect
              </Button>
              <Button
                size="sm"
                variant="ghost"
                startIcon={<Unlink size={13} />}
                onClick={handleDisconnect}
                disabled={disconnect.isPending}
              >
                Disconnect
              </Button>
            </div>
          </>
        ) : (
          <>
            <div style={statusInfo}>
              <AlertTriangle size={16} color="var(--vn-warning-solid)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                Not connected
              </span>
            </div>
            <Button size="sm" onClick={handleConnect} disabled={connecting}>
              {connecting ? 'Connecting…' : 'Connect Microsoft 365'}
            </Button>
          </>
        )}
      </div>

      {popupBlocked && (
        <Alert tone="warning" title="Popup blocked">
          Allow popups for this site and try connecting again.
        </Alert>
      )}
      {connectError && (
        <Alert tone="danger" title={connectError} />
      )}

      {/* Destination + target dropdowns — visible only after connected */}
      {connected && (
        <>
          <TeamsDestinationToggle value={targetType} onChange={setTargetType} />

          {targetType === 'channel' ? (
            <>
              <Field label="Team" required>
                {(p) =>
                  loadingTeams ? (
                    <Skeleton style={{ height: 36, width: '100%', maxWidth: 340 }} />
                  ) : (
                    <Select value={teamId} onValueChange={setTeamId}>
                      <SelectTrigger {...p} style={{ maxWidth: 340 }} aria-label="Select team">
                        <SelectValue placeholder="Select a team…" />
                      </SelectTrigger>
                      <SelectContent>
                        {teamsData.length === 0 ? (
                          <SelectItem value="__none__" disabled>No teams found</SelectItem>
                        ) : (
                          teamsData.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.displayName}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )
                }
              </Field>

              <Field label="Channel" required>
                {(p) =>
                  loadingChannels ? (
                    <Skeleton style={{ height: 36, width: '100%', maxWidth: 340 }} />
                  ) : (
                    <Select value={msChannelId} onValueChange={setMsChannelId} disabled={!teamId}>
                      <SelectTrigger {...p} style={{ maxWidth: 340 }} aria-label="Select channel">
                        <SelectValue placeholder={teamId ? 'Select a channel…' : 'Select a team first'} />
                      </SelectTrigger>
                      <SelectContent>
                        {channelsData.length === 0 ? (
                          <SelectItem value="__none__" disabled>
                            {teamId ? 'No channels found' : 'Select a team first'}
                          </SelectItem>
                        ) : (
                          channelsData.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.displayName}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )
                }
              </Field>
            </>
          ) : (
            <Field label="Chat" required>
              {(p) =>
                loadingChats ? (
                  <Skeleton style={{ height: 36, width: '100%', maxWidth: 340 }} />
                ) : (
                  <Select value={chatId} onValueChange={setChatId}>
                    <SelectTrigger {...p} style={{ maxWidth: 340 }} aria-label="Select chat">
                      <SelectValue placeholder="Select a chat…" />
                    </SelectTrigger>
                    <SelectContent>
                      {chatsData.length === 0 ? (
                        <SelectItem value="__none__" disabled>No chats found</SelectItem>
                      ) : (
                        chatsData.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.displayName}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )
              }
            </Field>
          )}

          {update.error && (
            <Alert tone="danger" title={hcExtractError(update.error, 'Save failed')} />
          )}

          <div>
            <Button size="sm" onClick={handleSave} disabled={!canSave}>
              Save configuration
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

const statusRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--vn-space-3)',
  flexWrap: 'wrap',
  padding: 'var(--vn-space-3)',
  border: '1px solid var(--vn-border-default)',
  borderRadius: 'var(--vn-radius-md)',
  background: 'var(--vn-bg-subtle)',
};

const statusInfo: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--vn-space-2)',
  minWidth: 0,
};

const statusActions: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--vn-space-2)',
  flexShrink: 0,
};
